/**
 * Runtime Environment Configuration Helper
 * 
 * This module provides a centralized way to access environment variables.
 * It prioritizes runtime config (window._env_) over build-time config (process.env).
 * 
 * This solves the Vercel deployment issue where environment variables were
 * baked into the bundle at build time and couldn't be changed at deployment.
 * 
 * Usage:
 *   import { getBackendUrl, getCdnUrl, getGoogleClientId } from '@/utils/env';
 *   const backendUrl = getBackendUrl();
 */

/**
 * Get the backend API URL
 * @returns {string} Backend URL (e.g., 'https://api.magic-moments.ai')
 */
export function getBackendUrl() {
  // Priority 1: Runtime config from emergency override (set by index.html script)
  if (window.process && window.process.env && window.process.env.REACT_APP_BACKEND_URL) {
    const url = window.process.env.REACT_APP_BACKEND_URL;
    if (url && !url.startsWith('${')) {
      return url;
    }
  }
  
  // Priority 2: Runtime config from env-config.js (injected at deployment)
  if (window._env_ && window._env_.REACT_APP_BACKEND_URL && !window._env_.REACT_APP_BACKEND_URL.startsWith('${')) {
    return window._env_.REACT_APP_BACKEND_URL;
  }
  
  // Priority 3: Hostname-based detection (works even if env vars fail)
  const hostname = window.location.hostname;
  
  // Emergent development environment
  if (hostname.includes('emergentagent.com') || hostname.includes('preview.') || hostname === 'localhost' || hostname === '127.0.0.1') {
    console.log('🌐 Detected Emergent/local development, using Emergent backend');
    return 'https://vidu-wizard.preview.emergentagent.com';
  }
  
  // Production domain
  if (hostname === 'magic-moments.ai' || hostname === 'www.magic-moments.ai' || hostname.endsWith('.vercel.app')) {
    console.log('🌐 Detected production domain, using api.magic-moments.ai');
    return 'https://api.magic-moments.ai';
  }
  
  // Priority 4: Final fallback for production
  console.warn('⚠️ Using final fallback backend URL');
  return 'https://api.magic-moments.ai';
}

/**
 * Get the CDN URL
 * @returns {string} CDN URL (e.g., 'https://cdn.magic-moments.ai')
 */
export function getCdnUrl() {
  // Priority 1: Runtime config from emergency override
  if (window.process && window.process.env && window.process.env.REACT_APP_CDN_URL) {
    return window.process.env.REACT_APP_CDN_URL;
  }
  
  // Priority 2: Runtime config from env-config.js
  if (window._env_ && window._env_.REACT_APP_CDN_URL && !window._env_.REACT_APP_CDN_URL.startsWith('${')) {
    return window._env_.REACT_APP_CDN_URL;
  }
  
  // Priority 3: Fallback
  return 'https://cdn.magic-moments.ai';
}

/**
 * Get the Google Client ID
 * @returns {string} Google OAuth Client ID
 */
export function getGoogleClientId() {
  // Priority 1: Runtime config from emergency override
  if (window.process && window.process.env && window.process.env.REACT_APP_GOOGLE_CLIENT_ID) {
    return window.process.env.REACT_APP_GOOGLE_CLIENT_ID;
  }
  
  // Priority 2: Runtime config from env-config.js
  if (window._env_ && window._env_.REACT_APP_GOOGLE_CLIENT_ID && !window._env_.REACT_APP_GOOGLE_CLIENT_ID.startsWith('${')) {
    return window._env_.REACT_APP_GOOGLE_CLIENT_ID;
  }
  
  // Priority 3: Fallback
  return '';
}

/**
 * Check if running in production environment
 * @returns {boolean} True if in production
 */
export function isProduction() {
  return process.env.NODE_ENV === 'production';
}

/**
 * Check if running in development environment
 * @returns {boolean} True if in development
 */
export function isDevelopment() {
  return process.env.NODE_ENV === 'development';
}

/**
 * Log current environment configuration (for debugging)
 */
export function logEnvironmentConfig() {
  console.group('🔧 Environment Configuration');
  console.log('Backend URL:', getBackendUrl());
  console.log('CDN URL:', getCdnUrl());
  console.log('Has Google Client ID:', !!getGoogleClientId());
  console.log('Environment:', process.env.NODE_ENV);
  console.log('Runtime config loaded:', !!window._env_);
  console.groupEnd();
}

// Export a default object with all functions
export default {
  getBackendUrl,
  getCdnUrl,
  getGoogleClientId,
  isProduction,
  isDevelopment,
  logEnvironmentConfig
};
